<template>
  <div class="article-page">
    <!-- 面包屑 -->
    <nav class="breadcrumb-nav">
      <router-link to="/">首页</router-link>
      <span class="separator">/</span>
      <router-link to="/research">研究中心</router-link>
      <span class="separator">/</span>
      <span class="current">能源行业数字化转型标杆案例集</span>
    </nav>

    <article class="container">
      <!-- 头部 -->
      <header class="article-header">
        <div class="article-tags">
          <span class="tag">案例研究</span>
          <span class="tag">能源行业</span>
          <span class="tag">数字化转型</span>
        </div>
        <h1 class="article-title">能源行业数字化转型标杆案例集</h1>
        <p class="article-subtitle">国家能源集团 · 中石油 · 国家电网 · 从战略规划到落地实施的全流程复盘</p>
        <div class="article-meta">
          <span><i class="far fa-calendar-alt"></i> 2026-01-15</span>
          <span><i class="far fa-clock"></i> 阅读约 18 分钟</span>
          <span><i class="far fa-user"></i> 古杉研究中心</span>
        </div>
      </header>

      <!-- 导读 -->
      <section class="intro-card">
        <div class="intro-icon">⚡</div>
        <div>
          <h3>导读</h3>
          <p>能源行业是我国经济的"压舱石"，也是数字化转型的重点领域。本报告深度拆解国家能源集团、中国石油、国家电网、中国华能、中国大唐五大央企的数字化转型实践，从战略、架构、数据、AI、组织五个维度进行全流程复盘，提炼可复制的方法论与关键教训。</p>
        </div>
      </section>

      <!-- 目录 -->
      <nav class="toc">
        <h3>📑 目录</h3>
        <ul>
          <li><a href="#section1">一、能源行业数字化转型全景</a></li>
          <li><a href="#section2">二、案例一：国家能源集团 —— 数据驱动的智慧能源</a></li>
          <li><a href="#section3">三、案例二：中国石油 —— 油气全产业链数字化</a></li>
          <li><a href="#section4">四、案例三：国家电网 —— 新型电力系统的数字底座</a></li>
          <li><a href="#section5">五、案例四：中国华能 —— AI赋能发电运营</a></li>
          <li><a href="#section6">六、案例五：中国大唐 —— 审计风控数字化标杆</a></li>
          <li><a href="#section7">七、方法论总结与借鉴</a></li>
        </ul>
      </nav>

      <!-- 正文 -->
      <div class="article-body">

        <!-- 第一章 -->
        <section id="section1" class="chapter">
          <h2>一、能源行业数字化转型全景</h2>
          <p>能源行业具有资产重、链条长、安全要求高的特点，数字化转型既有巨大的价值空间，也面临独特的挑战。</p>

          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-number">92%</div>
              <div class="stat-label">的能源央企已制定数字化转型战略</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">3.5%</div>
              <div class="stat-label">平均数字化投入占营收比例</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">45%</div>
              <div class="stat-label">的生产环节已实现数字化覆盖</div>
            </div>
            <div class="stat-card">
              <div class="stat-number">2.8x</div>
              <div class="stat-label">数字化领先企业的劳动生产率倍数</div>
            </div>
          </div>

          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🏭</div>
              <div>
                <h4>能源行业数字化转型的五大场景</h4>
                <p>智能生产运营 · 安全环保监管 · 智慧供应链 · 智能营销服务 · 经营管理决策</p>
              </div>
            </div>
          </div>

          <div class="comparison-table">
            <h4>能源行业数字化转型成熟度对标</h4>
            <table>
              <thead>
                <tr>
                  <th>维度</th>
                  <th>行业平均</th>
                  <th>标杆企业</th>
                  <th>差距分析</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>数据治理能力</td>
                  <td>DCMM 2级</td>
                  <td>DCMM 4级</td>
                  <td>主数据标准与数据质量差距最大</td>
                </tr>
                <tr>
                  <td>智能化应用</td>
                  <td>局部试点</td>
                  <td>规模化部署</td>
                  <td>AI模型的业务适配度不足</td>
                </tr>
                <tr>
                  <td>审计数字化</td>
                  <td>覆盖率 20%</td>
                  <td>覆盖率 85%</td>
                  <td>持续审计体系尚未建立</td>
                </tr>
                <tr>
                  <td>安全生产</td>
                  <td>事后响应</td>
                  <td>实时预警</td>
                  <td>IoT感知层覆盖不足</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- 第二章 -->
        <section id="section2" class="chapter">
          <h2>二、案例一：国家能源集团 —— 数据驱动的智慧能源</h2>

          <div class="case-profile">
            <div class="profile-grid">
              <div class="profile-item">
                <span class="profile-label">企业规模</span>
                <span class="profile-value">世界500强第69位</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">员工总数</span>
                <span class="profile-value">33万+</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">下属企业</span>
                <span class="profile-value">600+ 矿井、电厂</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">转型周期</span>
                <span class="profile-value">2021-2025（一期）</span>
              </div>
            </div>
          </div>

          <h3>2.1 转型背景与挑战</h3>
          <div class="challenge-cards">
            <div class="challenge-card">
              <div class="challenge-icon">🔴</div>
              <h4>数据孤岛严重</h4>
              <p>200+业务系统独立运行，数据格式不统一，跨板块数据打通率不足15%。</p>
            </div>
            <div class="challenge-card">
              <div class="challenge-icon">🔴</div>
              <h4>安全管控滞后</h4>
              <p>矿井安全依赖人工巡检，事故预警响应平均耗时45分钟，远不能满足实时要求。</p>
            </div>
            <div class="challenge-card">
              <div class="challenge-icon">🔴</div>
              <h4>能耗优化空间大</h4>
              <p>火电机组能耗差异达8%，煤矿综合能效低于国际先进水平12个百分点。</p>
            </div>
            <div class="challenge-card">
              <div class="challenge-icon">🔴</div>
              <h4>审计覆盖不足</h4>
              <p>传统审计仅覆盖15%子公司，经济责任审计周期长达3个月。</p>
            </div>
          </div>

          <h3>2.2 战略规划与顶层设计</h3>
          <div class="timeline-horizontal">
            <div class="tl-item" v-for="item in guonengTimeline" :key="item.year">
              <div class="tl-year">{{ item.year }}</div>
              <div class="tl-content">{{ item.desc }}</div>
            </div>
          </div>

          <h3>2.3 核心建设成果</h3>
          <div class="pillar-section">
            <div class="pillar-card">
              <div class="pillar-icon">📊</div>
              <h4>集团数据中台</h4>
              <p>统一数据标准3200+条，汇聚200+系统数据，日处理数据量50TB+</p>
              <ul>
                <li>主数据管理覆盖供应商、客户、物资、组织</li>
                <li>数据质量评分从58分提升至92分</li>
                <li>实现DCMM四级认证</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🤖</div>
              <h4>智能安全平台</h4>
              <p>IoT传感器覆盖100%矿井+80%电厂关键设备</p>
              <ul>
                <li>瓦斯浓度实时监测，预警响应缩短至30秒</li>
                <li>AI视觉识别违规操作，准确率98.5%</li>
                <li>设备故障预测提前48小时预警</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">⚡</div>
              <h4>智慧能效系统</h4>
              <p>基于AI优化锅炉燃烧效率和机组运行参数</p>
              <ul>
                <li>火电机组供电煤耗降低6.2g/kWh</li>
                <li>年节约标准煤120万吨</li>
                <li>碳排放减少320万吨</li>
              </ul>
            </div>
          </div>

          <div class="result-banner">
            <h4>🏆 转型成效总览</h4>
            <div class="result-metrics">
              <span>安全事故 ↓45%</span>
              <span>能耗降低 12%</span>
              <span>调度效率 +60%</span>
              <span>数据共享率 85%</span>
              <span>审计覆盖率 82%</span>
            </div>
          </div>
        </section>

        <!-- 第三章 -->
        <section id="section3" class="chapter">
          <h2>三、案例二：中国石油 —— 油气全产业链数字化</h2>

          <div class="case-profile">
            <div class="profile-grid">
              <div class="profile-item">
                <span class="profile-label">企业规模</span>
                <span class="profile-value">世界500强第4位</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">员工总数</span>
                <span class="profile-value">43万+</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">业务覆盖</span>
                <span class="profile-value">油气勘探 → 终端零售</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">数字化投入</span>
                <span class="profile-value">年均80亿+</span>
              </div>
            </div>
          </div>

          <h3>3.1 "数字中石油"战略框架</h3>
          <div class="visual-block blue-gradient">
            <div class="visual-block-inner">
              <div class="visual-icon">🛢️</div>
              <div>
                <h4>一中台 · 两平台 · 三张网 · N个场景</h4>
                <p>以数据中台为核心，构建工业互联网平台+AI平台，打通生产网、经营网、服务网，赋能N个业务场景。</p>
              </div>
            </div>
          </div>

          <h3>3.2 关键落地场景</h3>
          <div class="scene-grid">
            <div class="scene-card">
              <div class="scene-header explore">
                <span class="scene-tag">勘探开发</span>
              </div>
              <div class="scene-body">
                <h4>AI地震资料解释</h4>
                <p>深度学习模型自动解释地震数据，识别油气藏，解释效率提升15倍，发现新增可采储量2.3亿吨。</p>
                <div class="scene-metrics">
                  <span>解释效率 +15x</span>
                  <span>开发成本 ↓22%</span>
                </div>
              </div>
            </div>
            <div class="scene-card">
              <div class="scene-header refine">
                <span class="scene-tag">炼化生产</span>
              </div>
              <div class="scene-body">
                <h4>数字孪生炼化工厂</h4>
                <p>1:1构建炼化装置数字孪生体，实时模拟优化工艺参数，每套装置年均降本3000万元。</p>
                <div class="scene-metrics">
                  <span>工艺优化 ↑8%</span>
                  <span>停车减少 60%</span>
                </div>
              </div>
            </div>
            <div class="scene-card">
              <div class="scene-header pipeline">
                <span class="scene-tag">管道运输</span>
              </div>
              <div class="scene-body">
                <h4>智能管道管控平台</h4>
                <p>8万公里管道实现全线光纤感知，AI自动检测泄漏，定位精度达到±10米。</p>
                <div class="scene-metrics">
                  <span>泄漏检测 秒级</span>
                  <span>巡线成本 ↓70%</span>
                </div>
              </div>
            </div>
            <div class="scene-card">
              <div class="scene-header retail">
                <span class="scene-tag">终端零售</span>
              </div>
              <div class="scene-body">
                <h4>智慧加油站</h4>
                <p>2.5万座加油站数字化改造，AI客户画像 + 智能定价 + 无感支付，非油品收入增长35%。</p>
                <div class="scene-metrics">
                  <span>非油收入 +35%</span>
                  <span>客户留存 +28%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="result-banner">
            <h4>🏆 转型成效总览</h4>
            <div class="result-metrics">
              <span>勘探效率 +15x</span>
              <span>炼化能耗 ↓8%</span>
              <span>管道安全 99.99%</span>
              <span>年降本增效 50亿+</span>
            </div>
          </div>
        </section>

        <!-- 第四章 -->
        <section id="section4" class="chapter">
          <h2>四、案例三：国家电网 —— 新型电力系统的数字底座</h2>

          <div class="case-profile">
            <div class="profile-grid">
              <div class="profile-item">
                <span class="profile-label">企业规模</span>
                <span class="profile-value">世界500强第3位</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">服务用户</span>
                <span class="profile-value">11亿+</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">电网规模</span>
                <span class="profile-value">110万公里输电线路</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">核心平台</span>
                <span class="profile-value">国网云 + 数据中台</span>
              </div>
            </div>
          </div>

          <h3>4.1 新型电力系统的数字化挑战</h3>
          <p>随着"双碳"目标推进，新能源占比快速提升，电力系统面临"源-网-荷-储"全链条的数字化新需求。</p>

          <div class="pillar-section">
            <div class="pillar-card">
              <div class="pillar-icon">🌞</div>
              <h4>源侧：新能源并网</h4>
              <p>AI预测风光出力，精度提升至95%</p>
              <ul>
                <li>风电功率预测误差从15%降至5%</li>
                <li>光伏发电量预测精度达96%</li>
                <li>新能源消纳率提升至98.2%</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🔌</div>
              <h4>网侧：智能调度</h4>
              <p>基于AI的电网调度替代人工决策</p>
              <ul>
                <li>自动化调度覆盖率85%</li>
                <li>故障恢复时间缩短80%</li>
                <li>线损率降低0.3个百分点</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🏠</div>
              <h4>荷侧：需求响应</h4>
              <p>智能电表+用户画像实现精准负荷管理</p>
              <ul>
                <li>5.5亿智能电表全量接入</li>
                <li>需求响应容量达5000万kW</li>
                <li>客户满意度提升至92%</li>
              </ul>
            </div>
          </div>

          <h3>4.2 数据治理体系建设</h3>
          <div class="highlight-box">
            <div class="highlight-icon">📊</div>
            <div>
              <h4>国家电网数据治理"四统一"</h4>
              <p>统一数据标准（18000+数据元素）· 统一主数据管理（设备/客户/组织）· 统一数据质量规则（5000+规则）· 统一数据安全分级（四级分类标准）。已通过DCMM四级认证，数据资产编目120万+数据项。</p>
            </div>
          </div>

          <div class="result-banner">
            <h4>🏆 转型成效总览</h4>
            <div class="result-metrics">
              <span>新能源消纳 98.2%</span>
              <span>调度自动化 85%</span>
              <span>客户满意度 92%</span>
              <span>线损降低 0.3%</span>
            </div>
          </div>
        </section>

        <!-- 第五章 -->
        <section id="section5" class="chapter">
          <h2>五、案例四：中国华能 —— AI赋能发电运营</h2>

          <div class="case-profile">
            <div class="profile-grid">
              <div class="profile-item">
                <span class="profile-label">企业定位</span>
                <span class="profile-value">全球最大发电企业之一</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">装机容量</span>
                <span class="profile-value">2.2亿千瓦</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">数字化重点</span>
                <span class="profile-value">AI+发电运营</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">核心成果</span>
                <span class="profile-value">"华能大脑"</span>
              </div>
            </div>
          </div>

          <h3>5.1 "华能大脑"智能运营平台</h3>
          <p>中国华能率先在发电行业引入大模型技术，构建"华能大脑"智能运营平台，覆盖发电运行优化、设备健康管理、燃料智能调度三大核心场景。</p>

          <div class="scene-grid two-cols">
            <div class="scene-card">
              <div class="scene-header huaneng-blue">
                <span class="scene-tag">AI运行优化</span>
              </div>
              <div class="scene-body">
                <h4>机组智能运行调控</h4>
                <p>基于深度强化学习的锅炉燃烧优化系统，实时调整300+运行参数，实现"无人值守"自动控制。火电机组供电煤耗降低4.8g/kWh，年节煤15万吨。</p>
                <div class="scene-metrics">
                  <span>煤耗 ↓4.8g/kWh</span>
                  <span>NOx排放 ↓25%</span>
                </div>
              </div>
            </div>
            <div class="scene-card">
              <div class="scene-header huaneng-green">
                <span class="scene-tag">设备健康管理</span>
              </div>
              <div class="scene-body">
                <h4>预测性维护系统</h4>
                <p>基于振动分析、温度监测、油液分析等多源数据构建设备健康度模型，提前14天预警关键设备故障，非计划停机减少65%。</p>
                <div class="scene-metrics">
                  <span>故障预警 提前14天</span>
                  <span>非停减少 65%</span>
                </div>
              </div>
            </div>
          </div>

          <div class="result-banner">
            <h4>🏆 转型成效总览</h4>
            <div class="result-metrics">
              <span>供电煤耗 ↓4.8g</span>
              <span>年节煤 15万吨</span>
              <span>非停减少 65%</span>
              <span>运维人效 +40%</span>
            </div>
          </div>
        </section>

        <!-- 第六章 -->
        <section id="section6" class="chapter">
          <h2>六、案例五：中国大唐 —— 审计风控数字化标杆</h2>

          <div class="case-profile">
            <div class="profile-grid">
              <div class="profile-item">
                <span class="profile-label">企业定位</span>
                <span class="profile-value">五大发电集团之一</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">下属企业</span>
                <span class="profile-value">700+ 家</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">数字化重点</span>
                <span class="profile-value">审计风控一体化</span>
              </div>
              <div class="profile-item">
                <span class="profile-label">合作伙伴</span>
                <span class="profile-value">古杉数据</span>
              </div>
            </div>
          </div>

          <h3>6.1 审计风控数字化建设</h3>
          <p>中国大唐集团与古杉数据合作，构建了覆盖全集团的审计风控数字化体系，实现从传统审计到智能审计的全面跃升。</p>

          <div class="pillar-section">
            <div class="pillar-card">
              <div class="pillar-icon">🔍</div>
              <h4>智能审计平台</h4>
              <p>全量数据审计替代抽样审计</p>
              <ul>
                <li>审计覆盖率从18%提升至88%</li>
                <li>200+审计模型自动运行</li>
                <li>疑点识别准确率95%</li>
                <li>审计报告自动生成</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">🛡️</div>
              <h4>风控合规平台</h4>
              <p>穿透式风险监控体系</p>
              <ul>
                <li>风险指标600+项实时监测</li>
                <li>合规检查自动化率75%</li>
                <li>风险预警提前度达72小时</li>
                <li>关联交易穿透式监管</li>
              </ul>
            </div>
            <div class="pillar-card">
              <div class="pillar-icon">👤</div>
              <h4>干部履职画像</h4>
              <p>经济责任审计智能评价</p>
              <ul>
                <li>12维度干部履职评价模型</li>
                <li>画像生成时间从3周缩至1天</li>
                <li>覆盖集团全部中层以上干部</li>
                <li>与组织人事系统数据贯通</li>
              </ul>
            </div>
          </div>

          <div class="highlight-box">
            <div class="highlight-icon">💡</div>
            <div>
              <h4>关键经验：审计数据集市先行</h4>
              <p>中国大唐的经验表明，审计数字化的核心基础是"审计数据集市"——将财务、业务、合同、人事等多源数据汇聚清洗后，形成审计专用数据集。只有数据准备充分，上层的审计模型和智能分析才能发挥真正价值。</p>
            </div>
          </div>

          <div class="result-banner">
            <h4>🏆 转型成效总览</h4>
            <div class="result-metrics">
              <span>审计覆盖率 88%</span>
              <span>审计效率 +5x</span>
              <span>风险预警 72h</span>
              <span>合规检查自动化 75%</span>
            </div>
          </div>
        </section>

        <!-- 第七章 -->
        <section id="section7" class="chapter">
          <h2>七、方法论总结与借鉴</h2>
          <p>通过对五大能源央企的深入分析，我们总结出能源行业数字化转型的六大关键成功因素：</p>

          <div class="method-grid">
            <div class="method-card" v-for="(item, index) in methods" :key="index">
              <div class="method-number">{{ index + 1 }}</div>
              <h4>{{ item.title }}</h4>
              <p>{{ item.desc }}</p>
            </div>
          </div>

          <div class="comparison-table">
            <h4>五大案例核心指标对比</h4>
            <table>
              <thead>
                <tr>
                  <th>指标</th>
                  <th>国家能源</th>
                  <th>中国石油</th>
                  <th>国家电网</th>
                  <th>中国华能</th>
                  <th>中国大唐</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>转型侧重</td>
                  <td>安全+能效</td>
                  <td>全链条数字化</td>
                  <td>新型电力系统</td>
                  <td>AI运营</td>
                  <td>审计风控</td>
                </tr>
                <tr>
                  <td>数据治理</td>
                  <td>DCMM 4级</td>
                  <td>DCMM 3级</td>
                  <td>DCMM 4级</td>
                  <td>DCMM 3级</td>
                  <td>DCMM 3级</td>
                </tr>
                <tr>
                  <td>AI应用深度</td>
                  <td>★★★★</td>
                  <td>★★★★★</td>
                  <td>★★★★</td>
                  <td>★★★★★</td>
                  <td>★★★</td>
                </tr>
                <tr>
                  <td>年化降本</td>
                  <td>30亿+</td>
                  <td>50亿+</td>
                  <td>40亿+</td>
                  <td>15亿+</td>
                  <td>8亿+</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="visual-block blue-gradient" style="margin-top: 32px;">
            <div class="visual-block-inner">
              <div class="visual-icon">📝</div>
              <div>
                <h4>报告结语</h4>
                <p>能源行业的数字化转型已从"要不要做"进入"怎么做好"的深水区。安全生产、能效优化、审计风控是三大核心价值领域，数据治理是共同基础，AI大模型正在成为新的加速器。每家企业需要根据自身特点，找到最佳切入点，稳步推进。</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- CTA -->
      <section class="cta-section">
        <h2>了解更多能源行业数字化方案</h2>
        <p>古杉数据深耕能源行业审计风控与数据治理，为央企提供定制化解决方案</p>
        <div class="cta-buttons">
          <router-link to="/appointment" class="btn-primary">预约行业专家</router-link>
          <router-link to="/industry" class="btn-outline">查看行业方案</router-link>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
export default {
  name: 'EnergyCaseStudies',
  data() {
    return {
      guonengTimeline: [
        { year: '2021', desc: '成立数字化转型领导小组，发布"十四五"数字化专项规划' },
        { year: '2022', desc: '建设集团数据中台一期，统一数据标准体系' },
        { year: '2023', desc: 'IoT感知层全面覆盖，智能安全平台上线' },
        { year: '2024', desc: 'AI能效优化系统部署，审计数字化平台建设' },
        { year: '2025', desc: '大模型试点应用，数据治理通过DCMM四级认证' }
      ],
      methods: [
        { title: '一把手挂帅，战略先行', desc: '五家企业均由集团主要领导亲自挂帅数字化转型，将其纳入集团战略和绩效考核体系。' },
        { title: '数据治理是基础', desc: '没有高质量的数据，AI和大数据分析都是空中楼阁。每家标杆企业都将数据治理放在最高优先级。' },
        { title: '场景驱动，价值导向', desc: '聚焦安全、能效、审计等高价值场景，快速验证价值后再推广，避免全面铺开导致资源分散。' },
        { title: '平台化思维', desc: '构建数据中台、AI平台等基础能力平台，实现能力复用，降低边际成本。' },
        { title: '内外部协同', desc: '引入古杉数据等专业服务商深度合作，"内部团队+外部专家"模式加速落地。' },
        { title: '人才先行', desc: '大规模数字化人才培训，培养既懂能源业务又懂技术的复合型人才。' }
      ]
    }
  }
}
</script>

<style scoped>
.article-page { background: #fff; min-height: 100vh; }

.breadcrumb-nav { max-width: 960px; margin: 0 auto; padding: 20px 24px; font-size: 14px; color: #5A5E66; }
.breadcrumb-nav a { color: #1A73E8; text-decoration: none; }
.breadcrumb-nav a:hover { text-decoration: underline; }
.breadcrumb-nav .separator { margin: 0 8px; color: #ccc; }
.breadcrumb-nav .current { color: #0E1F52; }

.container { max-width: 960px; margin: 0 auto; padding: 0 24px 60px; }

.article-header { text-align: center; padding: 40px 0 32px; border-bottom: 1px solid #E8ECF0; margin-bottom: 40px; }
.article-header .article-tags { display: flex; justify-content: center; gap: 8px; margin-bottom: 20px; }
.article-header .tag { background: #EBF3FE; color: #1A73E8; padding: 4px 16px; border-radius: 20px; font-size: 13px; font-weight: 500; }
.article-title { font-size: 2.2rem; font-weight: 800; color: #0E1F52; line-height: 1.3; margin-bottom: 16px; }
.article-subtitle { font-size: 1.1rem; color: #5A5E66; margin-bottom: 20px; }
.article-meta { display: flex; justify-content: center; gap: 24px; font-size: 14px; color: #8C8C8C; }
.article-meta i { margin-right: 4px; }

.intro-card { display: flex; gap: 16px; background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-left: 4px solid #1A73E8; border-radius: 12px; padding: 24px; margin-bottom: 40px; }
.intro-card .intro-icon { font-size: 2rem; flex-shrink: 0; }
.intro-card h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.intro-card p { font-size: 15px; color: #3D4150; line-height: 1.7; }

.toc { background: #F6F8FC; border-radius: 12px; padding: 24px 28px; margin-bottom: 48px; }
.toc h3 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.toc ul { list-style: none; padding: 0; }
.toc li { margin-bottom: 8px; }
.toc a { color: #1A73E8; text-decoration: none; font-size: 15px; }
.toc a:hover { color: #0E1F52; text-decoration: underline; }

.article-body { font-size: 16px; line-height: 1.85; color: #3D4150; }
.article-body p { margin-bottom: 18px; }
.chapter { margin-bottom: 56px; }
.chapter h2 { font-size: 1.6rem; font-weight: 800; color: #0E1F52; margin-bottom: 24px; padding-bottom: 12px; border-bottom: 3px solid #1A73E8; display: inline-block; }
.chapter h3 { font-size: 1.3rem; font-weight: 700; color: #0E1F52; margin: 28px 0 16px; }

.visual-block { border-radius: 12px; padding: 24px; margin: 24px 0; }
.blue-gradient { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; }
.visual-block-inner { display: flex; gap: 16px; align-items: center; }
.visual-icon { font-size: 2.5rem; flex-shrink: 0; }
.visual-block h4 { font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.visual-block p { font-size: 15px; line-height: 1.6; opacity: 0.9; margin: 0; }

.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; margin: 28px 0; }
.stat-card { background: linear-gradient(135deg, #EBF3FE 0%, #F6F8FC 100%); border-radius: 12px; padding: 24px; text-align: center; border: 1px solid #D6E4F0; }
.stat-number { font-size: 2.2rem; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.stat-label { font-size: 13px; color: #5A5E66; line-height: 1.5; }

.comparison-table { margin: 28px 0; overflow-x: auto; }
.comparison-table h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 12px; }
.comparison-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
.comparison-table th { background: #0E1F52; color: #fff; padding: 12px 16px; text-align: left; font-weight: 600; }
.comparison-table td { padding: 12px 16px; border-bottom: 1px solid #E8ECF0; color: #3D4150; }
.comparison-table tr:nth-child(even) td { background: #F6F8FC; }

/* 企业档案 */
.case-profile { background: #F6F8FC; border-radius: 12px; padding: 24px; margin: 20px 0 28px; border: 1px solid #E8ECF0; }
.profile-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.profile-item { text-align: center; }
.profile-label { display: block; font-size: 12px; color: #8C8C8C; margin-bottom: 6px; font-weight: 500; }
.profile-value { display: block; font-size: 15px; font-weight: 700; color: #0E1F52; }

/* 挑战卡片 */
.challenge-cards { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 20px 0; }
.challenge-card { background: #FFF5F5; border-radius: 12px; padding: 20px; border: 1px solid #FED7D7; }
.challenge-icon { font-size: 1.5rem; margin-bottom: 8px; }
.challenge-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.challenge-card p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* 水平时间线 */
.timeline-horizontal { display: flex; gap: 4px; margin: 24px 0; overflow-x: auto; }
.tl-item { flex: 1; min-width: 140px; background: #F6F8FC; border-radius: 12px; padding: 16px; border: 1px solid #E8ECF0; text-align: center; position: relative; }
.tl-year { font-size: 18px; font-weight: 900; color: #1A73E8; margin-bottom: 8px; }
.tl-content { font-size: 13px; color: #5A5E66; line-height: 1.5; }

/* 支柱卡片 */
.pillar-section { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.pillar-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; transition: all 0.3s; }
.pillar-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.pillar-icon { font-size: 2rem; margin-bottom: 12px; }
.pillar-card h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 6px; }
.pillar-card > p { font-size: 14px; color: #5A5E66; margin-bottom: 12px; }
.pillar-card ul { list-style: none; padding: 0; margin: 0; }
.pillar-card li { font-size: 13px; color: #5A5E66; padding: 3px 0 3px 14px; position: relative; }
.pillar-card li::before { content: '▸'; position: absolute; left: 0; color: #1A73E8; font-weight: 700; }

/* 成果横幅 */
.result-banner { background: linear-gradient(135deg, #EBF3FE 0%, #F0F7FF 100%); border-radius: 12px; padding: 24px; margin: 28px 0; border: 1px solid #D6E4F0; text-align: center; }
.result-banner h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 16px; }
.result-metrics { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.result-metrics span { background: #1A73E8; color: #fff; padding: 8px 18px; border-radius: 20px; font-size: 14px; font-weight: 600; }

/* 场景卡片 */
.scene-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; margin: 24px 0; }
.scene-grid.two-cols { grid-template-columns: repeat(2, 1fr); }
.scene-card { border-radius: 12px; overflow: hidden; border: 1px solid #E8ECF0; transition: all 0.3s; }
.scene-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(31,63,143,0.12); }
.scene-header { padding: 14px 20px; color: #fff; }
.scene-header.explore { background: linear-gradient(135deg, #0E4EAD 0%, #0E1F52 100%); }
.scene-header.refine { background: linear-gradient(135deg, #B45309 0%, #78350F 100%); }
.scene-header.pipeline { background: linear-gradient(135deg, #059669 0%, #064E3B 100%); }
.scene-header.retail { background: linear-gradient(135deg, #7C3AED 0%, #4C1D95 100%); }
.scene-header.huaneng-blue { background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); }
.scene-header.huaneng-green { background: linear-gradient(135deg, #059669 0%, #064E3B 100%); }
.scene-tag { background: rgba(255,255,255,0.2); padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
.scene-body { padding: 20px; }
.scene-body h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.scene-body p { font-size: 14px; color: #5A5E66; line-height: 1.6; margin-bottom: 12px; }
.scene-metrics { display: flex; gap: 8px; flex-wrap: wrap; }
.scene-metrics span { background: #EBF3FE; color: #1A73E8; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; }

/* 高亮 */
.highlight-box { display: flex; gap: 16px; background: #FFF7E6; border-left: 4px solid #FAAD14; border-radius: 12px; padding: 24px; margin: 28px 0; }
.highlight-icon { font-size: 2rem; flex-shrink: 0; }
.highlight-box h4 { font-size: 16px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.highlight-box p { font-size: 15px; color: #3D4150; line-height: 1.7; margin: 0; }

/* 方法论 */
.method-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin: 24px 0; }
.method-card { background: #F6F8FC; border-radius: 12px; padding: 24px; border: 1px solid #E8ECF0; position: relative; transition: all 0.3s; }
.method-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(31,63,143,0.1); }
.method-number { width: 32px; height: 32px; background: #1A73E8; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 14px; margin-bottom: 12px; }
.method-card h4 { font-size: 15px; font-weight: 700; color: #0E1F52; margin-bottom: 8px; }
.method-card p { font-size: 13px; color: #5A5E66; line-height: 1.6; margin: 0; }

/* CTA */
.cta-section { text-align: center; background: linear-gradient(135deg, #1F3F8F 0%, #0E1F52 100%); color: #fff; border-radius: 16px; padding: 48px 32px; margin-bottom: 60px; }
.cta-section h2 { font-size: 1.8rem; font-weight: 800; margin-bottom: 12px; }
.cta-section p { font-size: 16px; opacity: 0.85; margin-bottom: 28px; }
.cta-buttons { display: flex; justify-content: center; gap: 16px; }
.btn-primary { background: #1A73E8; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; transition: all 0.3s; }
.btn-primary:hover { background: #1557B0; }
.btn-outline { background: transparent; color: #fff; padding: 12px 32px; border-radius: 8px; font-size: 16px; font-weight: 600; text-decoration: none; border: 2px solid rgba(255,255,255,0.5); transition: all 0.3s; }
.btn-outline:hover { border-color: #fff; background: rgba(255,255,255,0.1); }

@media (max-width: 768px) {
  .article-title { font-size: 1.6rem; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .profile-grid { grid-template-columns: repeat(2, 1fr); }
  .challenge-cards { grid-template-columns: 1fr; }
  .pillar-section { grid-template-columns: 1fr; }
  .scene-grid { grid-template-columns: 1fr; }
  .method-grid { grid-template-columns: repeat(2, 1fr); }
  .timeline-horizontal { flex-direction: column; }
  .article-meta { flex-wrap: wrap; justify-content: center; }
  .cta-buttons { flex-direction: column; align-items: center; }
}
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; }
  .profile-grid { grid-template-columns: 1fr; }
  .method-grid { grid-template-columns: 1fr; }
}
</style>
